<template>
    <div>
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && component.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-model="component"
                            :edit-mode="editMode"
                            :validation="$v.component.value"
                            :server-errors="serverErrors"
                        />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div class="mb-3">
                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            :key="informationKey"
                            :item="component"
                            :sections="sections"
                            :edit-mode="editMode"
                            :campuses="component.campuses"
                            :campuses-error="campusesError"
                            @meta-change="onMetaChange"
                            @on-delete-confirm="onDeleteConfirm"
                            @on-campuses-change="onCampusesChange"
                        />
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else />
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import { StatusCodes as HTTP } from 'http-status-codes';
import AddEditForm from '@/components/homepages/AddEditForm';
import ItemInformation from '@/components/elements/ItemInformation';

export default {
    page: {
        title: 'Manage Homepage Components'
    },

    validations: {
        component: {
            campuses: {
                required
            },
            value: {
                newsbreaker: {
                    $each: {
                        text: {
                            required
                        },
                        link: {
                            required
                        }
                    }
                },
                tiles: {
                    $each: {
                        image: { required },
                        title: { required },
                        redirect: { required },
                        premiumOnly: { required },
                        premiumOnlyImage: {
                            requiredIf: (v, { premiumOnly }) =>
                                !!(!premiumOnly || (premiumOnly && v && v.id))
                        }
                    }
                },
                slides: {
                    $each: {
                        upperText: { required },
                        lowerText: { required },
                        buttonText: { required },
                        buttonLink: { required },
                        image: { required }
                    }
                }
            }
        }
    },

    components: {
        AddEditForm,
        ItemInformation
    },

    data() {
        const { type = null, id = null } = this.$route.params;

        return {
            informationKey: 0,
            component: {
                campuses: [],
                type
            },
            items: [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Homepage components',
                    to: '/homepage-components'
                },
                {
                    text: id ? 'Edit' : 'Add',
                    active: true
                }
            ],
            id,
            editMode: !!id,
            form: {
                loggedInOnly: false
            },
            serverErrors: []
        };
    },

    computed: {
        ...mapGetters({
            ALL_TYPES: 'homepageComponents/ALL_TYPES'
        }),

        sections() {
            const sections = {
                campuses: true,
                meta: true
            };

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        },

        title() {
            const { type = null, id = null } = this.$route.params;

            if (!type) {
                return '';
            }
            const componentType = this.ALL_TYPES.find(
                ({ value }) => value === type
            );

            return id
                ? `Edit ${componentType.text}`
                : `Add ${componentType.text}`;
        },

        campusesError() {
            return this.$getValidationErrorMessageHandler.handle(
                this.$v.component,
                this.serverErrors,
                'campuses'
            );
        }
    },

    async created() {
        if (this.editMode) {
            try {
                this.component = await this.getComponent(this.id);
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }

            return;
        }

        try {
            const { rows } = await this.getCampuses({ perPage: 5 });

            this.component.campuses = rows;
        } catch (error) {
            console.error(error);

            this.$toasterError();
        }
    },

    methods: {
        ...mapActions({
            getCampuses: 'campuses/index',
            addComponent: 'homepageComponents/store',
            deleteComponent: 'homepageComponents/destroy',
            updateComponent: 'homepageComponents/update',
            getComponent: 'homepageComponents/show'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            const campuses = this.component.campuses.map(campus => campus.id);
            const component = { ...this.component, campuses };

            try {
                if (!this.editMode) {
                    const added = await this.addComponent(component);

                    this.$router.push(
                        `/homepage-components/${added.type}/${added.id}/edit`
                    );
                } else {
                    const { updatedAt, type } = await this.updateComponent({
                        id: this.id,
                        data: component
                    });

                    this.component.updatedAt = updatedAt;

                    this.$toaster(`Homepage ${type} updated!`);
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteComponent(this.id);

                const { type } = this.$route.params;

                this.$router.push(`/homepage-components?type=${type}`);
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.UNPROCESSABLE_ENTITY && data) {
                        this.$toasterError(data);

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        onMetaChange(meta) {
            this.component.meta = { ...meta };
        },

        onCampusesChange(campuses) {
            this.component.campuses = [...campuses];
            this.informationKey++;
        }
    }
};
</script>
